/**
 * `SHELL-050`/`051`/`052`/`053`. Outcome, final score, goal completion and,
 * since Sprint 6 stage 3, stars earned. `stars` arrives as a prop rather
 * than being computed here from `result`/`goals` (sprint-8a stage 4):
 * `deriveStars` (`completion.ts`) needs the level's cumulative
 * `completedGoalSets` count, which lives in the save document, not in one
 * attempt's own `GameResult` -- stars are permanent (`PROG-043`), so what
 * this screen shows has to be the same number `Play.tsx`'s own call to
 * `recordCompletion` just persisted, read from that one call rather than
 * re-derived here from a second formula that could disagree with it.
 *
 * Goal labels are as game-agnostic as `Hud.tsx`'s own -- see that file's
 * header for why a `collect` goal's raw token, not a prettified name, is
 * what renders.
 *
 * The focus trap here is a general N-element version, not `ConfirmDialog`'s
 * fixed two-ref one: this screen offers two or three actions (`SHELL-052`
 * omits "next level" on the last one), so which elements exist is not
 * known in advance the way a confirm dialog's "cancel" and "confirm" always
 * are.
 */

import { useLayoutEffect, useRef } from 'preact/hooks';

import type { GameResult } from '@contracts/game-module';
import type { Goal } from '@contracts/level';
import { goalKey } from '@contracts/level';
import { focusScreenHeading } from '@shell/screen-transition';

function outcomeMessage(result: GameResult): string {
  switch (result.outcome) {
    case 'won':
      return 'You won!';
    case 'lost':
      return 'Level over.';
    case 'abandoned':
      return 'You left the level.';
  }
}

function goalLabel(goal: Goal): string {
  switch (goal.kind) {
    case 'score':
      return 'Score';
    case 'collect':
    case 'avoid':
      return goal.piece;
    case 'clear-obstacle':
      return goal.obstacle;
    case 'custom':
      return goal.label;
    case 'time':
      return 'Time';
  }
}

function goalTarget(goal: Goal): number {
  switch (goal.kind) {
    case 'score':
      return goal.target;
    case 'collect':
    case 'clear-obstacle':
      return goal.count;
    case 'custom':
      return goal.target;
    case 'avoid':
      return goal.limit;
    case 'time':
      return goal.limitMs;
  }
}

export interface ResultScreenProps {
  result: GameResult;
  goals: readonly Goal[];
  /** The level's cumulative star count, already computed and persisted -- see this file's own header for why it is a prop, not derived here. */
  stars: number;
  onReplay: () => void;
  /** `undefined` when `SHELL-052` says to omit it -- the last level in its ordered mode. */
  nextHref: string | undefined;
  levelSelectHref: string;
}

export function ResultScreen({
  result,
  goals,
  stars,
  onReplay,
  nextHref,
  levelSelectHref,
}: ResultScreenProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // SHELL-053. Focus moves in as the screen appears and returns to the
  // screen heading once it is gone, by any of the three exits -- the same
  // shape `ConfirmDialog`'s own effect uses, `useLayoutEffect` so it lands
  // before paint rather than a frame behind it.
  useLayoutEffect(() => {
    containerRef.current?.querySelector<HTMLElement>('a[href], button')?.focus();
    return () => {
      focusScreenHeading();
    };
  }, []);

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') return;
    const focusables = [
      ...(containerRef.current?.querySelectorAll<HTMLElement>('a[href], button') ?? []),
    ];
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div class="result" ref={containerRef} onKeyDown={onKeyDown}>
      <p class="result__outcome">{outcomeMessage(result)}</p>
      <p class="result__score">
        Score: <span class="result__score-value">{result.score}</span>
      </p>
      {result.outcome === 'won' && (
        // Text, not a glyph run -- WCAG 1.4.1 (not colour alone) and this
        // project's own "no invented prettification" rule (Hud.tsx's own
        // header) both point the same way: "2 of 3 stars" is unambiguous
        // read aloud or at a glance, a row of star glyphs is not without
        // also stating the count somewhere.
        <p class="result__stars">{stars} of 3 stars</p>
      )}
      <ul class="result__goals">
        {goals.map((goal) => {
          const key = goalKey(goal);
          const target = goalTarget(goal);
          const achieved = Math.min(result.goalProgress[key] ?? 0, target);
          return (
            <li key={key}>
              {goalLabel(goal)}: {achieved} / {target}
              {achieved >= target ? ' ✓' : ''}
            </li>
          );
        })}
      </ul>
      <nav class="screen-nav">
        <button type="button" onClick={onReplay}>
          Replay
        </button>
        {nextHref !== undefined && (
          <a class="nav-link" href={nextHref}>
            Next level
          </a>
        )}
        <a class="nav-link" href={levelSelectHref}>
          Back to level select
        </a>
      </nav>
    </div>
  );
}
